import * as React from "react";
import { useState } from "react";
import { Button, Paper, Typography, Alert, Snackbar, Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { TabPanelHeader } from "../../components/TabPanelHeader";
import { useGameConfigService } from "../../services/backend/GameConfigService";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { EnvironmentPicker } from "../../components/EnvironmentPicker";
import type { IEnvironment } from "../../types/EnvironmentTypes";

export interface DeployDataTabPanelProps {
  selectedBranch: string | undefined;
  toolboxOutput: string | undefined;
  onToolboxOutputUpdate: (toolboxOutput: string | undefined) => void;
}

export const DeployDataTabPanel = ({ selectedBranch, toolboxOutput, onToolboxOutputUpdate }: DeployDataTabPanelProps) => {
  const gameConfigService = useGameConfigService();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error" | "info" | "warning">("info");
  const [selectedEnvironment, setSelectedEnvironment] = useState<IEnvironment | undefined>(undefined);

  const handleEnvironmentSelected = (environment: IEnvironment) => {
    setSelectedEnvironment(environment);
  };

  const handleDeploy = async () => {
    if (!selectedBranch) {
      setAlertMessage("Please select a branch first");
      setAlertSeverity("error");
      return;
    }

    if (!selectedEnvironment) {
      setAlertMessage("Please select a target environment");
      setAlertSeverity("error");
      return;
    }

    try {
      setIsLoading(true);
      const deployResult = await gameConfigService.deployImportedData(
        selectedBranch,
        undefined,
        undefined,
        selectedEnvironment.name,
      );

      if (deployResult) {
        setAlertMessage("Data deployed successfully.");
        setAlertSeverity("success");
        if (deployResult.output) {
          onToolboxOutputUpdate(deployResult.output);
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to deploy data.");
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
    }
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  if (!selectedBranch) {
    return (
      <Paper sx={{ p: 2, height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography variant="subtitle1" color="textSecondary">
          Please select a branch to deploy data.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <TabPanelHeader>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={handleDeploy}
          sx={{ marginRight: 2, height: 44 }}
          disabled={!selectedBranch || !selectedEnvironment}
        >
          Deploy
        </Button>
      </TabPanelHeader>

      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Deploy Imported Data
        </Typography>
        <Typography variant="body2" paragraph>
          Deploy the imported game configuration data to the selected environment.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <EnvironmentPicker onEnvironmentSelected={handleEnvironmentSelected} />
        </Box>
      </Paper>

      {toolboxOutput && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Deploy Output
          </Typography>
          <pre style={{ whiteSpace: "pre-wrap" }}>{toolboxOutput}</pre>
        </Paper>
      )}

      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      {isLoading && <LoadingOverlay />}
    </>
  );
};
